import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { CurrencyPairCalendarLookupService } from "../lookup/model/impl/CurrencyPairCalendarLookupService";

import { RateCache } from "../rate/impl/RateCache";

import { BasePricingService } from "../lookup/pricing/impl/BasePricingService";
import { DealtPricingService } from "../lookup/pricing/impl/DealtPricingService";
import { CcyLookupService } from "../lookup/model/impl/CcyLookupService";
import { CcyPairLookupService } from "../lookup/model/impl/CcyPairLookupService";
import { ClientLookupService } from "../lookup/model/impl/ClientLookupService";
import { AccountLookupService } from "../lookup/model/impl/AccountLookupService";
import { TraderGroupLookupService } from "../lookup/model/impl/TraderGroupLookupService";
import { RateSourceLookupService } from "../lookup/model/impl/RateSourceLookupService";
import { AlgoStrategyLookupService } from "../lookup/model/impl/AlgoStrategyLookupService";
import { FixingSourceLookupService } from "../lookup/model/impl/FixingSourceLookupService";
import { SalesGroupLookupService } from "../lookup/model/impl/SalesGroupLookupService";
import { UserLookupService } from "../lookup/model/impl/UserLookupService";
import { SystemConfigPromiseService } from "shared/auth-services";
import { ProfilePromiseActionService } from "../profile/impl/ProfilePromiseActionService";

import { Injectable, Optional } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { OrdersTableDefinitionFactory } from "./OrdersTableDefinitionFactory";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { INBOX_TABLE_DEFINITION_VER } from "./TableDefinitionFactory.constants";
import { ClientModelPrivatization } from "./privatization/ClientModelPrivatization";
import { AccountModelPrivatization } from "./privatization/AccountModelPrivatization";
import { ClientTraderModelPrivatization } from "./privatization/ClientTraderModelPrivatization";
import { AtdlTooltipUiRep } from "./adtl/atdl-tooltip-ui-rep";
import { BfxSysConfigUtilService } from "shared/auth-services";
import { BfxConfigService } from "shared/auth-services/system-config/bfx-config.service";
import { CustomFieldLookupService } from "../lookup/model/impl/CustomFieldLookupService";
import { DIReasonHelper } from "./orders/DIReasonHelper";
import { IndexType } from "./IndexType";
import { BfxColDef, TableDefinition } from "./TableDefinition";
import { ColumnFilterType } from "./ColumnFilterType";

@Injectable()
export class InboxTableDefinitionFactory extends OrdersTableDefinitionFactory {
  protected diReasonHelper: DIReasonHelper;

  constructor(
    profileService: ProfilePromiseActionService,
    rateCache: RateCache,
    @Optional() rateSourceLookup: RateSourceLookupService,
    @Optional() myClientLookup: ClientLookupService,
    @Optional() myAccountLookup: AccountLookupService,
    @Optional() mySalesGroupLookup: SalesGroupLookupService,
    @Optional() myTraderGroupLookup: TraderGroupLookupService,
    @Optional() ccyLookupService: CcyLookupService,
    @Optional() ccyPairLookupService: CcyPairLookupService,
    @Optional() userLookupService: UserLookupService,
    @Optional() sysConfigService: SystemConfigPromiseService,
    @Optional() translateService: TranslateService,
    @Optional() myBasePricingService: BasePricingService,
    @Optional() myDealtPricingService: DealtPricingService,
    @Optional() fixingSourceLookupService: FixingSourceLookupService,
    @Optional() algoStrategyLookupService: AlgoStrategyLookupService,
    @Optional()
    currencyPairCalendarLookupService: CurrencyPairCalendarLookupService,
    @Optional() cmp: ClientModelPrivatization,
    @Optional() amp: AccountModelPrivatization,
    @Optional() ctmp: ClientTraderModelPrivatization,
    @Optional() atdlTooltipUiRep: AtdlTooltipUiRep,
    @Optional() sysConfigUtil: BfxSysConfigUtilService,
    @Optional() configService: BfxConfigService,
    @Optional() customFieldLookupService: CustomFieldLookupService,
  ) {
    super(
      profileService,
      rateCache,
      rateSourceLookup,
      myClientLookup,
      myAccountLookup,
      mySalesGroupLookup,
      myTraderGroupLookup,
      ccyLookupService,
      ccyPairLookupService,
      userLookupService,
      sysConfigService,
      translateService,
      myBasePricingService,
      myDealtPricingService,
      fixingSourceLookupService,
      algoStrategyLookupService,
      currencyPairCalendarLookupService,
      cmp,
      amp,
      ctmp,
      atdlTooltipUiRep,
      sysConfigUtil,
      configService,
      customFieldLookupService,
    );
    this.diReasonHelper = new DIReasonHelper(translateService, ccyLookupService);
  }

  public createDef(): TableDefinition {
    let td = super.createDef();
    td.algoTableDefinition = true;
    let inboxOsIdColumn: BfxColDef = this.initColdDef();
    inboxOsIdColumn.field = "inboxOpsId";
    inboxOsIdColumn.sortingOrder = ["asc", "desc"];
    inboxOsIdColumn.headerName = "Ops ID";
    inboxOsIdColumn.headerCheckboxSelection = false;
    inboxOsIdColumn.columnFilterType = ColumnFilterType.NONE;
    inboxOsIdColumn.suppressSizeToFit = true;
    if (!this.restoreFromConfig(inboxOsIdColumn)) {
      inboxOsIdColumn.position = this.position.next();
    }
    inboxOsIdColumn.minWidth = this.oneWs;
    inboxOsIdColumn.width = this.eightWs;
    // tooltip renderer requires this
    inboxOsIdColumn.valueGetter = function (this: InboxTableDefinitionFactory, params) {
      let opsId: string = null;

      if (
        params != null &&
        params.data != null &&
        params.data instanceof CubiqTS.OrderDataImpl &&
        params.data.inboxOpsId != null &&
        CubiqTS.Long.isLong(params.data.inboxOpsId)
      ) {
        opsId = params.data.inboxOpsId.toString();
      }

      return opsId;
    }.bind(this);
    inboxOsIdColumn.tooltipValueGetter = function (this: InboxTableDefinitionFactory, params) {
      return params.value;
    }.bind(this);
    td.addColumn(inboxOsIdColumn);

    let inboxReasonColumn: BfxColDef = this.initColdDef();
    inboxReasonColumn.field = "inboxReason";
    inboxReasonColumn.sortingOrder = ["asc", "desc"];
    inboxReasonColumn.headerName = "Reason";
    inboxReasonColumn.headerCheckboxSelection = false;
    inboxReasonColumn.columnFilterType = ColumnFilterType.NONE;
    inboxReasonColumn.minWidth = this.oneWs;
    inboxReasonColumn.width = this.wrapEllipsisWs;

    inboxReasonColumn.valueGetter = function (this: InboxTableDefinitionFactory, params) {
      let inboxReason: CubiqTS.Errors = null;

      if (
        params != null &&
        params.data != null &&
        params.data instanceof CubiqTS.OrderDataImpl &&
        params.data.inboxReason != null &&
        params.data.inboxReason instanceof CubiqTS.ErrorsImpl
      ) {
        inboxReason = params.data.inboxReason;
      }

      return inboxReason;
    }.bind(this);
    let inboxReasonFormatterFn = function (this: InboxTableDefinitionFactory, params) {
      let inboxReason: string = null;

      if (
        params != null &&
        params.data != null &&
        params.data instanceof CubiqTS.OrderDataImpl &&
        params.data.inboxReason != null &&
        params.data.inboxReason instanceof CubiqTS.ErrorsImpl
      ) {
        inboxReason = this.diReasonHelper.retrieveData(params.data, "inboxReason", true);
      }

      return inboxReason;
    }.bind(this);
    inboxReasonColumn.valueFormatter = inboxReasonFormatterFn;
    inboxReasonColumn.indexType = IndexType.AgGridDefault;
    inboxReasonColumn.comparator = function (
      this: InboxTableDefinitionFactory,
      valueA,
      valueB,
      nodeA,
      nodeB,
    ) {
      let formattedA = valueA != null ? inboxReasonFormatterFn(nodeA) : "";
      let formattedB = valueB != null ? inboxReasonFormatterFn(nodeB) : "";

      if (formattedA == null) {
        formattedA = "";
      }
      if (formattedB == null) {
        formattedB = "";
      }

      return formattedA.localeCompare(formattedB);
    }.bind(this);
    let inboxReasonTooltipFn = function (this: InboxTableDefinitionFactory, params) {
      return this.diReasonHelper.retrieveTooltip(params);
    }.bind(this);
    inboxReasonColumn.getQuickFilterText = inboxReasonTooltipFn;
    inboxReasonColumn.tooltipValueGetter = inboxReasonTooltipFn;

    if (!this.restoreFromConfig(inboxReasonColumn)) {
      inboxReasonColumn.position = this.position.next();
    }

    td.addColumn(inboxReasonColumn);
    return td;
  }

  public getProfileSettingKey(): string {
    return super.getProfileSettingKey() + "/INBOX";
  }

  public getVersionIdentifier(): string {
    return INBOX_TABLE_DEFINITION_VER;
  }
}
